import React, { createContext, useContext, useMemo } from "react";
import { MergeState } from "@/common/lib/api/visitMerges";
import { useMergeStates } from "@/state/hooks/useVisitMerges";

type MergeStatesValue = { states: Record<string, MergeState>; available: boolean; updatedAt: number };

// null means no provider is mounted above the reader.
const MergeStatesContext = createContext<MergeStatesValue | null>(null);

/** One merge-state request for every visit on screen. keepWhileLoading: see useMergeStates. */
export function MergeStatesProvider({
    ids,
    keepWhileLoading,
    children,
}: {
    ids: string[];
    keepWhileLoading?: boolean;
    children: React.ReactNode;
}) {
    const key = ids.join(",");
    const stableIds = useMemo(() => Array.from(new Set(key ? key.split(",") : [])).sort(), [key]);
    const value = useMergeStates(stableIds, { keepWhileLoading });
    return <MergeStatesContext.Provider value={value}>{children}</MergeStatesContext.Provider>;
}

/**
 * Merge state for one visit. Without a provider above, it fetches that visit on its own
 * (React Query shares the request with a provider holding exactly the same ids).
 */
export function useMergeState(id: string | undefined): {
    state: MergeState | null;
    available: boolean;
    updatedAt: number;
} {
    const ctx = useContext(MergeStatesContext);
    const own = useMergeStates(!ctx && id ? [id] : []);
    const { states, available, updatedAt } = ctx ?? own;
    return { state: id ? (states[id] ?? null) : null, available, updatedAt: updatedAt ?? 0 };
}
